import { useState, useEffect, useCallback } from 'react';

export type ViewportType = 'mobile' | 'tablet' | 'desktop';

export interface LayoutConfig {
  viewport: ViewportType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  legendPosition: 'bottom' | 'left' | 'right';
  mapContainerClass: string;
  legendContainerClass: string;
  containerLayoutClass: string;
}

export interface UseResponsiveLayoutReturn {
  // Current viewport state
  viewportType: ViewportType;
  windowWidth: number;
  windowHeight: number;
  
  // Layout configuration
  layout: LayoutConfig;
  
  // Helper functions
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  
  // Layout classes and styles
  getMapContainerProps: () => { className: string; style?: React.CSSProperties };
  getLegendContainerProps: () => { className: string; style?: React.CSSProperties };
  getMainContainerProps: () => { className: string; style?: React.CSSProperties };
}

// Breakpoints (can be customized)
const BREAKPOINTS = {
  mobile: 768,   // 0-767px
  tablet: 1024,  // 768-1023px
  desktop: 1024  // 1024px+
};

export const useResponsiveLayout = (): UseResponsiveLayoutReturn => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [windowHeight, setWindowHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 768
  );

  // Determine viewport type based on width
  const getViewportType = useCallback((width: number): ViewportType => {
    if (width < BREAKPOINTS.mobile) return 'mobile';
    if (width < BREAKPOINTS.desktop) return 'tablet';
    return 'desktop';
  }, []);

  const viewportType = getViewportType(windowWidth);
  
  // Helper booleans
  const isMobile = viewportType === 'mobile';
  const isTablet = viewportType === 'tablet';
  const isDesktop = viewportType === 'desktop';

  // Generate layout configuration
  const layout: LayoutConfig = {
    viewport: viewportType,
    isMobile,
    isTablet,
    isDesktop,
    legendPosition: isMobile ? 'bottom' : isTablet ? 'left' : 'right',
    
    // Tailwind classes for different layouts
    mapContainerClass: isMobile 
      ? 'w-full h-[60vh]' // Mobile: full width, 60% height
      : isTablet 
        ? 'flex-1 h-full ml-4' // Tablet: flex grow, margin left
        : 'flex-1 h-full mr-4', // Desktop: flex grow, margin right
        
    legendContainerClass: isMobile
      ? 'w-full h-[40vh] overflow-y-auto' // Mobile: full width, 40% height, scrollable
      : isTablet
        ? 'w-80 h-full overflow-y-auto' // Tablet: fixed width 320px, full height
        : 'w-96 h-full overflow-y-auto', // Desktop: fixed width 384px, full height
        
    containerLayoutClass: isMobile
      ? 'flex flex-col h-screen' // Mobile: vertical stack
      : 'flex flex-row h-screen' // Tablet/Desktop: horizontal layout
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate props for containers
  const getMapContainerProps = useCallback(() => ({
    className: `${layout.mapContainerClass} relative`,
    style: isMobile ? {} : undefined
  }), [layout.mapContainerClass, isMobile]);

  const getLegendContainerProps = useCallback(() => ({
    className: `${layout.legendContainerClass} bg-white border`,
    style: isTablet ? { borderRight: '1px solid #e5e7eb' } : 
           isDesktop ? { borderLeft: '1px solid #e5e7eb' } : 
           { borderTop: '1px solid #e5e7eb' }
  }), [layout.legendContainerClass, isTablet, isDesktop]);

  const getMainContainerProps = useCallback(() => ({
    className: layout.containerLayoutClass,
    style: {}
  }), [layout.containerLayoutClass]);

  return {
    // Current viewport state
    viewportType,
    windowWidth,
    windowHeight,
    
    // Layout configuration
    layout,
    
    // Helper functions
    isMobile,
    isTablet,
    isDesktop,
    
    // Layout classes and styles
    getMapContainerProps,
    getLegendContainerProps,
    getMainContainerProps,
  };
};